import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
    explanation: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const TimeCalender: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showExplanation, setShowExplanation] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {
        setShowExplanation(false);
        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeSocialStudies/TimeCalender.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeSocialStudies/TimeCalender.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setShowExplanation(false);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
        else {
            setShowExplanation(true);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Time and Calendar for Class 3 (CBSE & ICSE) – Social Studies Lesson";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Learn about time, days, months, and calendars in this Class 3 Social Studies chapter. Understand how calendars help us plan and track important events for CBSE & ICSE students." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1 className="mb-5">⏰ Chapter 2 : Time & Calendar</h1>

                            <section>
                                <h2>🕰️ Understanding Time</h2>
                                <p>Time helps us measure events and organize our daily life. It tells us when to wake up, go to school, eat meals, and sleep. We use clocks and watches to see the current time.</p>
                            </section>

                            <section>
                                <h2>🛠️ Measuring Time</h2>
                                <ul>
                                    <li>In the past, people used the position of the Sun and shadows to tell time (sundials).</li>
                                    <li>Later, hourglasses and water clocks were invented.</li>
                                    <li>Now, we use digital and analog clocks to measure time accurately.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>📅 What is a Calendar?</h2>
                                <p>A calendar is a system of organizing days into weeks, months, and years. It helps us remember important dates, festivals, birthdays, and holidays.</p>
                            </section>

                            <section>
                                <h2>🗓️ Parts of a Calendar</h2>
                                <ul>
                                    <li><span className="highlight">Day</span> – 24 hours, from midnight to midnight.</li>
                                    <li><span className="highlight">Week</span> – 7 days (Monday to Sunday).</li>
                                    <li><span className="highlight">Month</span> – 28, 30, or 31 days.</li>
                                    <li><span className="highlight">Year</span> – 12 months or 365 days (366 in a leap year).</li>
                                </ul>
                            </section>

                            <section>
                                <h2>🔍 Importance of Time & Calendar</h2>
                                <ul>
                                    <li>Helps plan activities and manage schedules.</li>
                                    <li>Organizes festivals and events.</li>
                                    <li>Helps farmers know the right seasons for planting and harvesting.</li>
                                    <li>Important for schools, businesses, and travel planning.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>✅ Quick Recap</h2>
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Term</th>
                                            <th>Meaning</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Clock</td>
                                            <td>A device to tell time in hours, minutes, and seconds.</td>
                                        </tr>
                                        <tr>
                                            <td>Calendar</td>
                                            <td>A chart showing days, weeks, and months in a year.</td>
                                        </tr>
                                        <tr>
                                            <td>Leap Year</td>
                                            <td>A year with 366 days, occurring every 4 years.</td>
                                        </tr>
                                        <tr>
                                            <td>Sundial</td>
                                            <td>An ancient tool using shadows to measure time.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </section>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <header>⏰ Class 3 Social Studies – Time & Calendar</header>

                        <section>
                            <h2>🕰️ Understanding Time</h2>
                            <p>Time helps us measure events and organize our daily life. It tells us when to wake up, go to school, eat meals, and sleep. We use clocks and watches to see the current time.</p>
                        </section>

                        <section>
                            <h2>🛠️ Measuring Time</h2>
                            <ul>
                                <li>In the past, people used the position of the Sun and shadows to tell time (sundials).</li>
                                <li>Later, hourglasses and water clocks were invented.</li>
                                <li>Now, we use digital and analog clocks to measure time accurately.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>📅 What is a Calendar?</h2>
                            <p>A calendar is a system of organizing days into weeks, months, and years. It helps us remember important dates, festivals, birthdays, and holidays.</p>
                        </section>

                        <section>
                            <h2>🗓️ Parts of a Calendar</h2>
                            <ul>
                                <li><span className="highlight">Day</span> – 24 hours, from midnight to midnight.</li>
                                <li><span className="highlight">Week</span> – 7 days (Monday to Sunday).</li>
                                <li><span className="highlight">Month</span> – 28, 30, or 31 days.</li>
                                <li><span className="highlight">Year</span> – 12 months or 365 days (366 in a leap year).</li>
                            </ul>
                        </section>

                        <section>
                            <h2>🔍 Importance of Time & Calendar</h2>
                            <ul>
                                <li>Helps plan activities and manage schedules.</li>
                                <li>Organizes festivals and events.</li>
                                <li>Helps farmers know the right seasons for planting and harvesting.</li>
                                <li>Important for schools, businesses, and travel planning.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>✅ Quick Recap</h2>
                            <table>
                                <tr>
                                    <th>Term</th>
                                    <th>Meaning</th>
                                </tr>
                                <tr>
                                    <td>Clock</td>
                                    <td>A device to tell time in hours, minutes, and seconds.</td>
                                </tr>
                                <tr>
                                    <td>Calendar</td>
                                    <td>A chart showing days, weeks, and months in a year.</td>
                                </tr>
                                <tr>
                                    <td>Leap Year</td>
                                    <td>A year with 366 days, occurring every 4 years.</td>
                                </tr>
                                <tr>
                                    <td>Sundial</td>
                                    <td>An ancient tool using shadows to measure time.</td>
                                </tr>
                            </table>
                        </section>

                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}

                            {(showExplanation) ? (
                                <>
                                    <span><strong>Correct option:</strong> {currentQuestion.answer}</span>
                                    <div dangerouslySetInnerHTML={{ __html: currentQuestion.explanation }}>
                                    </div>
                                    <div className="mt-3 text-center">
                                        <button className="btn hero-btn" onClick={loadRandomSyllabusQuestions}>Next Question</button>
                                    </div>
                                </>
                            ) : (<></>)}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default TimeCalender;
